import { useState, useRef, useCallback, useEffect } from 'react'
import { MIN_CHARS, MAX_CHARS } from '@/hooks/useStreamGeneration'
import iconSparkle from '@/assets/images/icons/icon-sparkle.svg'
import './index.scss'

interface InputAreaProps {
  onGenerate: (input: string) => void
  disabled: boolean
}

const TEMPLATES = [
  { label: '📦 电商后台 - 批量导出订单', text: '为电商后台增加一个批量导出订单的功能，支持按时间、订单状态筛选导出' },
  { label: '📝 内容管理 - AI 文章摘要', text: '为内容管理系统增加AI自动生成文章摘要的功能，支持手动编辑修改' },
  { label: '🎧 客服系统 - 智能工单分配', text: '为客服系统增加智能工单分配功能，根据问题类型自动派发给对应组' },
  { label: '📊 BI平台 - 自然语言查数据', text: '为数据分析平台增加自然语言查询功能，用户可以直接输入问题查数据' },
  {
    label: '🏢 OA系统 - 请假审批流程',
    text: '为OA系统增加员工请假审批流程功能，支持员工提交请假申请、部门经理审批、人事归档',
  },
  {
    label: '📂 后台系统 - 批量删除/归档数据',
    text: '为后台管理系统增加数据批量删除功能，支持按条件筛选后批量操作，并支持回收站恢复机制',
  },
  {
    label: '🏃 健身APP - 每日打卡',
    text: '为健身APP增加一个用户每日打卡功能，支持文字和图片记录，用户打卡后可获得积分奖励',
  },
  { label: '📚 阅读APP - AI书籍摘要', text: '为阅读APP增加AI生成书籍内容摘要的功能，支持摘要长度调整和分享' },
  {
    label: '💬 社交APP - 动态分享',
    text: '为社交APP增加一个用户动态分享功能，支持发布图文、视频内容，好友可进行点赞和评论互动',
  },
  { label: '🛒 电商APP - 智能推荐', text: '为电商APP增加商品推荐功能，根据用户浏览历史和购买记录，在首页推荐相关商品' },
]

/** 输入区域组件 — 文本输入框 + 模板弹窗 + 生成按钮 + 字数校验 */
export default function InputArea({ onGenerate, disabled }: InputAreaProps) {
  const [input, setInput] = useState('')
  const [showPopover, setShowPopover] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const templateBtnRef = useRef<HTMLButtonElement>(null)

  const canGenerate = input.length >= MIN_CHARS && input.length <= MAX_CHARS && !disabled

  const handleGenerate = useCallback(() => {
    const trimmed = input.trim()
    if (!canGenerate || trimmed.length < MIN_CHARS) return
    onGenerate(trimmed)
    setInput('')
  }, [input, canGenerate, onGenerate])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleGenerate()
      }
    },
    [handleGenerate]
  )

  const handleTemplateSelect = useCallback((text: string) => {
    setInput(text)
    setShowPopover(false)
    setTimeout(() => {
      textareaRef.current?.focus()
      textareaRef.current?.setSelectionRange(text.length, text.length)
    }, 0)
  }, [])

  // 点击外部关闭弹窗
  useEffect(() => {
    if (!showPopover) return
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        templateBtnRef.current &&
        !templateBtnRef.current.contains(e.target as Node)
      ) {
        setShowPopover(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [showPopover])

  // 自动调整文本框高度（最大 100px 后滚动）
  const TEXTAREA_MAX_HEIGHT = 100
  const adjustHeight = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const scrollHeight = el.scrollHeight
    if (scrollHeight <= TEXTAREA_MAX_HEIGHT) {
      el.style.height = `${scrollHeight}px`
      el.style.overflowY = 'hidden'
    } else {
      el.style.height = `${TEXTAREA_MAX_HEIGHT}px`
      el.style.overflowY = 'auto'
    }
  }, [])

  useEffect(() => {
    adjustHeight()
  }, [input, adjustHeight])

  return (
    <div className="footer">
      <div className="input-container">
        <div className="input-wrapper">
          <textarea
            id="prompt-input"
            name="prompt"
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`例如：为电商后台增加一个批量导出订单的功能...，${MIN_CHARS}~${MAX_CHARS}字`}
            maxLength={MAX_CHARS}
          />
        </div>
        <div className="action-row">
          <div className="action-left">
            <button
              ref={templateBtnRef}
              className="btn-template"
              onClick={(e) => {
                e.stopPropagation()
                setShowPopover((v) => !v)
              }}
            >
              <span className="btn-icon" aria-hidden="true" />
              模板
            </button>
          </div>
          <button className="btn-generate" disabled={!canGenerate} onClick={handleGenerate}>
            <img src={iconSparkle} className="btn-icon" alt="" />
          </button>
        </div>

        {showPopover && (
          <div className="template-popover show" ref={popoverRef}>
            <div className="popover-list">
              {TEMPLATES.map((tpl) => (
                <div key={tpl.label} className="popover-item" onClick={() => handleTemplateSelect(tpl.text)}>
                  {tpl.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <p className="ai-disclaimer">内容由AI生成，请仔细甄别</p>
    </div>
  )
}
